import { useId, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { Campo, CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { HistoriaNaMesa } from '@/historias/mesa';
import { useSessao } from '@/modulos/sessao/consultas';

import { ConferenciaDeDocumentos } from './ConferenciaDeDocumentos';
import { apoioLigado } from './apoio';
import { useAlocar, useCadastrosDeApoio, useEncerrarAlocacao } from './consultas-frota';
import { MensagemDeErro } from './MensagemDeErro';
import type { EquipamentoDaFrota } from './frota';

/** Os campos, na ordem do formulario: o rotulo nao depende das obras. */
const CAMPOS_DA_ALOCACAO = [
  { rotulo: 'Obra', opcional: false },
  { rotulo: 'Cliente', opcional: true },
  { rotulo: 'Contrato', opcional: true },
  { rotulo: 'Centro de custo', opcional: true },
  { rotulo: 'Motivo', opcional: true },
] as const;

/**
 * Ferramenta 3 — alocacao com vigencia.
 *
 * ## Mover a maquina e uma acao, nao duas
 *
 * A pessoa que remaneja uma escavadeira nao pensa "encerrar a alocacao da obra
 * A e abrir a da obra B": ela pensa "a maquina foi para a Vale Verde". Entao a
 * tela tem UM botao, e a frase acima dele diz o que vai acontecer com a
 * vigencia aberta. O fechamento e a abertura acontecem no mesmo instante e na
 * mesma transacao — o banco recusaria qualquer coisa diferente, porque a
 * restricao de exclusao nao deixa a mesma maquina em dois lugares no mesmo
 * periodo.
 *
 * "Encerrar sem alocar" existe separado, como acao terciaria, porque e outra
 * intencao: a maquina volta ao patio e fica sem obra.
 *
 * ## Obra e obrigatoria; cliente, contrato e centro de custo nao
 *
 * E o que o modelo diz (`obra_id not null`, o resto nulavel), e e o que a obra
 * diz: uma maquina esta em algum lugar, e o contrato pode nem estar assinado
 * quando ela chega. O manual manda marcar o que e OPCIONAL, nunca o que e
 * obrigatorio — e e o que esta feito aqui.
 */
export function FormularioAlocacao({ equipamento }: { readonly equipamento: EquipamentoDaFrota }) {
  const base = useId();
  const sessao = useSessao();
  const apoio = useCadastrosDeApoio(apoioLigado(sessao.data?.permissoes, 'alocacao.gerir'));
  const alocar = useAlocar();
  const encerrar = useEncerrarAlocacao();

  const [obraId, setObraId] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [contratoId, setContratoId] = useState('');
  const [centroCustoId, setCentroCustoId] = useState('');
  const [motivo, setMotivo] = useState('');

  const cadastros = apoio.data;
  /* O BOTAO NAO FICA CINZA SEM DIZER POR QUE (dono, 31/10/2026: preencheu
     cliente e contrato, o botao nao respondeu e nada gravou). Ele fica
     clicavel, e a obra que falta aparece como erro no proprio campo. */
  const [faltaObra, setFaltaObra] = useState(false);
  const podeGravar = !alocar.isPending;

  /**
   * A OBRA TRAZ O CLIENTE DELA (26/10/2026).
   *
   * O dono: *"na obra já tem o cliente e ao selecionar obra não preencheu,
   * tive que fazer manual"*. O cadastro de obra guarda o cliente; repetir a
   * escolha aqui é pedir de novo o que o sistema já sabe — e é assim que o
   * cadastro de obra e o de alocação passam a discordar sobre o mesmo cliente.
   *
   * SOBRESCREVE o que estava escolhido, e é de propósito: trocar de obra troca
   * de cliente. Deixar o anterior guardaria a máquina na obra de um cliente
   * faturando para outro, que é o erro caro deste campo. A obra sem cliente
   * cadastrado LIMPA, pelo mesmo motivo — o campo continua aberto para quem
   * quiser escolher à mão.
   */
  function escolherObra(id: string) {
    setObraId(id);
    setFaltaObra(false);
    setClienteId((cadastros?.obras ?? []).find((o) => o.id === id)?.clienteId ?? '');
  }

  /* O CAMINHO DE VOLTA (31/10/2026): quem comeca pelo cliente ou pelo
     contrato recebe a obra quando ela e uma so daquele cliente. Com varias,
     a obra fica para escolher, e o erro do campo diz isso ao gravar. */
  function obraUnicaDoCliente(cliente: string): string {
    const doCliente = (cadastros?.obras ?? []).filter((o) => o.clienteId === cliente);
    return doCliente.length === 1 ? (doCliente[0]?.id ?? '') : '';
  }

  function escolherCliente(id: string) {
    setClienteId(id);
    if (obraId === '' && id !== '') {
      const unica = obraUnicaDoCliente(id);
      if (unica !== '') {
        setObraId(unica);
        setFaltaObra(false);
      }
    }
  }

  function escolherContrato(id: string) {
    setContratoId(id);
    const doContrato = (cadastros?.contratos ?? []).find((c) => c.id === id)?.clienteId ?? null;
    if (doContrato !== null && clienteId.length === 0) escolherCliente(doContrato);
    else if (doContrato !== null && obraId === '') {
      const unica = obraUnicaDoCliente(doContrato);
      if (unica !== '') {
        setObraId(unica);
        setFaltaObra(false);
      }
    }
  }

  /* Só quem aloca vê o formulário (prova do marco, 07/10/2026): a Oficina lê a
     alocação, e o histórico abaixo continua para ela. */
  if (!(sessao.data?.permissoes ?? []).includes('alocacao.gerir')) return null;

  if (apoio.isPending) {
    /* As opcoes ainda vem (p.32): os rotulos ja se sabem, e so os controles
       viram a barra de 40 px — no lugar e na altura do formulario. */
    return (
      <Carregando oQue="Carregando as obras e os centros de custo">
        <div aria-hidden="true" className="flex flex-col gap-4">
          <div className="grade-do-painel">
            {CAMPOS_DA_ALOCACAO.map((campo, i) => (
              <Campo key={campo.rotulo} id={`${base}-${String(i)}`} rotulo={campo.rotulo} opcional={campo.opcional}>
                <Barra className="h-[var(--controle-m)] w-full rounded-controle" />
              </Campo>
            ))}
          </div>
          <div className="flex justify-end border-t border-borda-sutil pt-3">
            <Barra className="h-[var(--controle-m)] w-40 rounded-controle" />
          </div>
        </div>
      </Carregando>
    );
  }

  if (apoio.isError) {
    return <MensagemDeErro erro={apoio.error} />;
  }

  if ((cadastros?.obras.length ?? 0) === 0) {
    return (
      <EstadoVazio
        titulo="Nenhuma obra cadastrada"
        descricao="Cadastre a primeira obra para poder alocar equipamentos. O cadastro de obras é do módulo de cadastros e governança."
      />
    );
  }

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(evento) => {
        evento.preventDefault();
        if (!podeGravar) return;
        if (obraId === '') {
          setFaltaObra(true);
          return;
        }
        alocar.mutate(
          {
            equipamentoId: equipamento.id,
            obraId,
            clienteId: clienteId || null,
            contratoId: contratoId || null,
            centroCustoId: centroCustoId || null,
            motivo: motivo.trim() || null,
            contexto: { fatos: [`na ${cadastros?.obras.find((o) => o.id === obraId)?.rotulo ?? 'obra escolhida'}`] },
          },
          {
            onSuccess: () => {
              setMotivo('');
            },
          },
        );
      }}
    >
      {/* Os campos na grade do painel (pedido do dono, 31/10/2026): lado a
          lado na janela larga, um embaixo do outro no painel estreito. */}
      <div className="grade-do-painel">
      <CampoSelecao
        rotulo="Obra"
        valor={obraId}
        aoMudar={escolherObra}
        rotuloVazio="Escolha a obra"
        erro={faltaObra ? 'Escolha a obra: a máquina é alocada numa obra, e o cliente e o contrato vão junto com ela.' : null}
        opcoes={(cadastros?.obras ?? []).map((o) => ({ valor: o.id, rotulo: o.rotulo }))}
      />

      <CampoSelecao
        rotulo="Cliente"
        opcional
        valor={clienteId}
        aoMudar={escolherCliente}
        rotuloVazio="Sem cliente"
        opcoes={(cadastros?.clientes ?? []).map((c) => ({ valor: c.id, rotulo: c.rotulo }))}
      />

      <CampoSelecao
        rotulo="Contrato"
        opcional
        valor={contratoId}
        aoMudar={escolherContrato}
        rotuloVazio="Sem contrato"
        opcoes={(cadastros?.contratos ?? []).map((c) => ({ valor: c.id, rotulo: c.rotulo }))}
      />

      <CampoSelecao
        rotulo="Centro de custo"
        opcional
        valor={centroCustoId}
        aoMudar={setCentroCustoId}
        rotuloVazio="Sem centro de custo"
        opcoes={(cadastros?.centrosDeCusto ?? []).map((c) => ({ valor: c.id, rotulo: c.rotulo }))}
      />

      <div className="campo-largo">
        <CampoTexto
          rotulo="Motivo"
          opcional
          valor={motivo}
          aoMudar={setMotivo}
          placeholder="Remanejamento para a frente de corte"
          ajuda="Entra na trilha de auditoria junto com a movimentação."
        />
      </div>
      </div>

      {/* Antes de gravar, o que a obra e o contrato exigem e falta (0066).
          Confere, nao barra. */}
      <ConferenciaDeDocumentos equipamentoId={equipamento.id} obraId={obraId} contratoId={contratoId || null} />

      <MensagemDeErro erro={alocar.error} />
      <MensagemDeErro erro={encerrar.error} />

      {/* Acoes ao FIM do formulario, a direita. Nao e a `barra-de-acoes`
          fixa: este formulario mora no corpo do painel de detalhe, que ja tem
          rodape fixo proprio (R01, p.29). Fixa aqui, ela parava 24 px acima do
          fim do painel, com campo aparecendo por baixo dela. */}
      <div className="flex items-center justify-end gap-2 border-t border-borda-sutil pt-3">
        {equipamento.alocacaoId ? (
          <Botao
            type="button"
            nivel="terciario"
            disabled={encerrar.isPending}
            onClick={() => {
              if (!equipamento.alocacaoId) return;
              encerrar.mutate({
                equipamentoId: equipamento.id,
                alocacaoId: equipamento.alocacaoId,
                motivo: motivo.trim() || null,
                obra: equipamento.obra,
              });
            }}
          >
            {encerrar.isPending ? 'Encerrando' : 'Encerrar alocação'}
          </Botao>
        ) : null}

        <HistoriaNaMesa codigo="BTN-EQP-06" className="flex">
          <Botao type="submit" nivel="secundario" disabled={!podeGravar}>
            {alocar.isPending ? 'Alocando' : 'Alocar equipamento'}
          </Botao>
        </HistoriaNaMesa>
      </div>

      <p className="txt-apoio text-tinta-apagada">
        {equipamento.obra
          ? `A alocação atual (${equipamento.obra}) é encerrada no mesmo instante em que a nova começa. O período anterior fica no histórico e continua apropriando os custos daquela obra.`
          : 'Esta máquina não está alocada. Esta será a primeira alocação do histórico.'}
      </p>
    </form>
  );
}
